// @ts-ignore
import React, { FC } from 'react';
import { connect } from 'react-redux';
import { BemMethod, createBem, PlatformTypes } from '@om-fe/utils';
import { AppState } from '@om-fe/core/core/types';
import getSlice from '@om-fe/core/utils/get-slice';
import {
    favoriteTeamsSlice,
    FavoriteTeamsStateModel,
} from '@om-fe/core/components/favorite-teams/favorite-teams.store';
import { favoriteSlice } from '@om-fe/core/components/favorite-events/favorite-state.store';
import { FavoritesStoreState } from '@om-fe/core/components/favorite-events/favorite-state.constants';
import FavoriteContent from './favorites-content';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { FavoritesNav } from '../favorites-nav/favorites-nav';
import FavoriteMixedMatchList from '../match-list/containers/favorite-match-list-v2/favorite-mixed-match-list';
import { DisciplineLiveStatus } from '@om-fe/models';

export enum FAVORITE_TAB {
    EVENT = 'EVENT',
    TOURNAMENT = 'TOURNAMENT',
    PARTICIPANT = 'PARTICIPANT',
    FIRST_TAB = '0',
}

interface FavoritePagePublicProps {
    readonly mainMarketsLimit?: number;
}

interface FavoritePageStoreProps {
    readonly isLarge?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly ready?: boolean;
    readonly favoriteTeamsList?: string[];
    readonly favoriteEventsList?: FavoritesStoreState;
    readonly lang: string;
}

type FavoritePageProps = FavoritePagePublicProps & FavoritePageStoreProps ;

const bem: BemMethod = createBem('FavoritePage');

function FavoriteContentTabComponent(
    {
        ready,
        favoriteTeamsList,
        favoriteEventsList,
        lang,
        deviceType,
        mainMarketsLimit,
    }: FavoritePageProps,
) {
    const { gettext } = useI18NContext();
    
    if (!ready) {
        return null;
    }
    const favoritEventsListNumber: number = Object.keys(favoriteEventsList)?.length;

    if (!favoriteTeamsList?.length && !favoritEventsListNumber) {

        return (
            <div className={bem({ extra: 'EmptyList' })}>{gettext('No matches currently available')}</div>
        );
    }

    if (favoriteTeamsList?.length) {
        return (
            <div className={bem()}>
                <FavoriteContent
                    mainMarketsLimit={mainMarketsLimit}
                    favoriteTeamsList={favoriteTeamsList!}
                    favoriteEvent={favoritEventsListNumber}
                    lang={lang}
                    activeTab={favoritEventsListNumber === 0 ? FAVORITE_TAB.FIRST_TAB : FAVORITE_TAB.EVENT}
                    activeType={favoritEventsListNumber === 0 ? FAVORITE_TAB.PARTICIPANT : FAVORITE_TAB.EVENT}
                    activeItem={favoritEventsListNumber === 0 ? favoriteTeamsList[0] : ''}
                    deviceType={deviceType}
                />
            </div>
        );
    }

    return (
        <div className={bem()}>
            <FavoritesNav
                list={[]}
                activeTab={FAVORITE_TAB.EVENT}
                favoriteEvent={favoriteEventsList}
                hasArrows={deviceType === 'desktop'}
            />
            <FavoriteMixedMatchList
                instanceId={`LiveEventFavorites`}
                liveStatus={DisciplineLiveStatus.BOTH}
                limitNumberOfEvents={20}
                mainMarketsLimit={mainMarketsLimit || 3}
            />
        </div>
    );
}

export const FavoriteContentTab: FC<FavoritePageProps> = connect<FavoritePageProps, undefined>(
    (state: AppState) => {
        const favoriteTeamSlice: FavoriteTeamsStateModel | undefined = getSlice<FavoriteTeamsStateModel>(state, favoriteTeamsSlice);
        const favoriteEventSlice: FavoritesStoreState | undefined = getSlice<FavoritesStoreState>(state, favoriteSlice);

        if (!favoriteTeamSlice && !favoriteEventSlice) {
            return {
                ready: false,
                favoritTeamsList: [],
                favoritEventsList: [],
                lang: state.config.lang,
            };
        }

        return {
            isLarge: state.breakpoints.large || state.breakpoints.xl,
            deviceType: state.deviceInfo?.platform.type,
            lang: state.config.lang,
            favoriteTeamsList: favoriteTeamSlice?.list ? favoriteTeamSlice.list : [],
            favoriteEventsList: favoriteEventSlice ? favoriteEventSlice.favoriteMatchItems : {},
            ready: favoriteTeamSlice?.ready && favoriteEventSlice?.ready,
        };
    }
)(FavoriteContentTabComponent);
